import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Send, QrCode, Download } from 'lucide-react';
import { useCopy } from '../hooks/useCopy';
import { buildTelegramShareLink } from '../ton/referral';
import { gaEvent } from '../analytics';
import { ReferralQr } from './ReferralQr';
import { renderQrDataUrl } from './qrRender';

interface ReferralLinkBlockProps {
  /** Short label for this link variant (e.g. "Telegram" / "Сайт"). */
  label: string;
  /** Icon shown next to the label. */
  icon: React.ReactNode;
  /** One-line hint describing where this link leads referrals. */
  hint: string;
  /** The shareable URL. */
  link: string;
  /** Referrer key, used to build the Telegram share caption link. */
  referrerKey: number;
  /**
   * Action shown below the QR. `'share'` (Telegram surface) opens the native
   * share sheet with the QR image attached; `'download'` (website) saves the
   * QR as a PNG.
   */
  action: 'share' | 'download';
}

/** Reads the QR canvas as a PNG File, or null when unavailable/tainted. */
async function canvasToPngFile(canvas: HTMLCanvasElement, name: string): Promise<File | null> {
  try {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) return null;
    return new File([blob], name, { type: 'image/png' });
  } catch {
    return null;
  }
}

/**
 * A single referral-link variant: the link text, a Copy button (which also
 * reveals the styled QR), a QR toggle, and — below the QR — a share button that
 * attaches the QR image via the Web Share API (falling back to a Telegram share
 * deep link when file sharing isn't supported).
 */
export function ReferralLinkBlock({
  label,
  icon,
  hint,
  link,
  referrerKey,
  action,
}: ReferralLinkBlockProps) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  const [showQr, setShowQr] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const caption = t('share.caption');
  const tgShareHref = buildTelegramShareLink(referrerKey, link, caption);

  const handleDownload = async () => {
    const url = await renderQrDataUrl(link, 1024);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pyramida-qr.png';
    a.click();
  };

  const handleShare = async () => {
    gaEvent('share_ref_link');
    const tg = window.Telegram?.WebApp;
    const canvas = canvasRef.current;

    // Inside a Telegram Mini App: try the Web Share API with the QR image
    // first (some Android webviews support it), but the reliable path is
    // Telegram's own `openTelegramLink` — it fires the native
    // `web_app_open_tg_link` event and opens the forward/share sheet with the
    // link + caption. We must NOT use `window.open` here: the Telegram in-app
    // webview blocks pop-ups, which is exactly why sharing did nothing before.
    if (typeof tg?.openTelegramLink === 'function') {
      const file = canvas ? await canvasToPngFile(canvas, 'pyramida-qr.png') : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            title: 'Pyramida',
            text: `${caption}\n${link}`,
            files: [file],
          });
          return;
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          // Any other failure → fall through to the native Telegram share.
        }
      }
      tg.openTelegramLink(tgShareHref);
      return;
    }

    // Outside Telegram (e.g. a desktop browser preview): Web Share, then a
    // plain new tab to the Telegram share dialog as a last resort.
    const file = canvas ? await canvasToPngFile(canvas, 'pyramida-qr.png') : null;
    const shareData: ShareData = { title: 'Pyramida', text: `${caption}\n${link}`, url: link };

    if (file && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ ...shareData, files: [file] });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }

    window.open(tgShareHref, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="ref-variant">
      <div className="ref-variant__head">
        <span className="ref-variant__label">
          {icon} {label}
        </span>
        <span className="ref-variant__hint">{hint}</span>
      </div>

      <div className="referral-link">{link}</div>

      <div className="referral-actions">
        <button
          type="button"
          className="refresh-btn"
          onClick={() => {
            gaEvent('share_ref_link');
            void copy(link);
            setShowQr(true);
          }}
          aria-label={t('refLink.copyAria', { label })}
        >
          {copied ? (
            <>
              <Check size={15} aria-hidden="true" /> {t('refLink.copied')}
            </>
          ) : (
            <>
              <Copy size={15} aria-hidden="true" /> {t('refLink.copy')}
            </>
          )}
        </button>

        <button
          type="button"
          className="refresh-btn"
          onClick={() => setShowQr((v) => !v)}
          aria-expanded={showQr}
          aria-label={showQr ? t('refLink.hideQrAria') : t('refLink.showQrAria')}
        >
          <QrCode size={15} aria-hidden="true" /> {showQr ? t('refLink.hideQr') : t('refLink.qr')}
        </button>
      </div>

      {showQr && (
        <div className="referral-qr-wrap">
          <ReferralQr ref={canvasRef} value={link} size={500} />
          <p className="referral-qr-cap">{t('refLink.qrCaption')}</p>
          {action === 'share' ? (
            <button type="button" className="share-btn" onClick={() => void handleShare()}>
              <Send size={16} aria-hidden="true" /> {t('refLink.share')}
            </button>
          ) : (
            <button type="button" className="share-btn" onClick={() => void handleDownload()}>
              <Download size={16} aria-hidden="true" /> {t('refLink.download')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
